import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Import your data file exactly as requested
import { newPropertiesData } from '../components/home/NewProperties';

const SLIDE_INTERVAL = 4000;
const NAV_SECTIONS = ['videos', 'overview', 'pricing', 'specifications', 'amenities', 'about', 'floor-plans', 'location-plan', 'layout', 'ground-floor', 'typical-floor'];

// --- SVG Icons ---
const FloorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
);
const FittingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></svg>
);
const WallIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="12" x2="21" y2="12"></line><line x1="12" y1="12" x2="12" y2="21"></line></svg>
);
const DefaultIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
);
const ChevronUp = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"></path></svg>
);
const ChevronDown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"></path></svg>
);

const getSpecIcon = (category) => {
  const norm = category.toLowerCase();
  if (norm.includes('floor') || norm.includes('counter')) return <FloorIcon />;
  if (norm.includes('fitting')) return <FittingIcon />;
  if (norm.includes('wall') || norm.includes('ceiling')) return <WallIcon />;
  return <DefaultIcon />;
};

// Standard configurations for features not explicitly stored in NewProperties
const DEFAULT_SPECS = [
  {
    category: "Floor & Counter",
    details: [
      { label: "Living/Dining", value: "Vitrified Tiles" },
      { label: "Master Bedroom", value: "Vitrified Flooring" },
      { label: "Kitchen", value: "Vitrified Tiles" },
      { label: "Toilet", value: "Anti-Skid Tiles" }
    ]
  },
  {
    category: "Fitting",
    details: [
      { label: "Toilets", value: "Branded CP Fittings" },
      { label: "Kitchen", value: "Granite Platform with SS Sink" }
    ]
  },
  {
    category: "Wall & Ceiling",
    details: [
      { label: "Interior", value: "Gypsum Finish" },
      { label: "Kitchen", value: "Glazed Tiles Dado" }
    ]
  }
];

const DEFAULT_AMENITIES = [
  { label: "Borewell", img: "https://api.awasindia.com/Images/Amen/1736843992719.jpg" },
  { label: "Road Facing", img: "https://api.awasindia.com/Images/Amen/1736848166765.png" },
  { label: "CCTV", img: "https://api.awasindia.com/Images/Amen/1736847793912.png" },
  { label: "Fire Alarm", img: "https://api.awasindia.com/Images/Amen/1736847914109.jpg" },
  { label: "Gated Entry", img: "https://api.awasindia.com/Images/Amen/1736847984705.jpg" },
  { label: "Parking", img: "https://api.awasindia.com/Images/Amen/1736787135550.jpg" },
  { label: "Gym", img: "https://api.awasindia.com/Images/Amen/1736787155862.png" },
  { label: "Pool", img: "https://api.awasindia.com/Images/Amen/1736787176781.png" }
];

const DEFAULT_PLANS = {
  locationPlan: 'https://www.mhada.gov.in/sites/default/files/inline-images/khoni-location_0.jpg',
  layout: 'https://www.mhada.gov.in/sites/default/files/inline-images/khoni-layout_0.jpg',
  groundFloor: 'https://www.mhada.gov.in/sites/default/files/inline-images/ground-floor_2.jpg',
  typicalFloor: 'https://www.mhada.gov.in/sites/default/files/inline-images/typical-floor_2.jpg'
};

export default function PropertyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  // --- Dynamic Data Extraction from imported newPropertiesData ---
  // Find the exact property clicked, or fallback to the first one
  const baseProperty = newPropertiesData.find(p => p.id.toString() === id) || newPropertiesData[0];

  // Construct a detailed property object using the imported data
  const property = {
    name: baseProperty.title,
    location: baseProperty.location,
    possession: 'December/2026', 
    status: 'Under Construction',
    reraStatus: 'MHADA Approved',
    maintenance: 'As per society',
    commercialSpace: 'None',
    ownership: 'Leasehold',
    propertyStatus: 'New Project',
    elevator: 'Yes',
    category: 'Residential',
    type: baseProperty.type,
    gatedSociety: 'Yes',
    negotiable: 'No',
    loanFacility: '90% Available',
    documents: 'Clear Title',
    dedicatedParking: 'Yes',
    openParking: 'Yes',
    about: baseProperty.description,
    whatsappNumber: '919702850777',
    agencyName: baseProperty.builder,
    images: [
      baseProperty.image,
      'https://sesplindia.in/photos/m2.webp',
      'https://sesplindia.in/photos/m1.jpg'
    ],
    videos: [
      { id: 'v1', title: `${baseProperty.title} Site Tour`, embedUrl: 'https://www.youtube.com/embed/m2aPqZMtktw' }
    ],
    units: [
      { 
        id: `unit-${baseProperty.id}`, 
        name: baseProperty.type, 
        carpet: baseProperty.description.includes('321') ? '321.84 Sq.ft' : '321.00 Sq.ft', // Extracts roughly from description
        price: baseProperty.price, 
        status: 'Available', 
        floorPlanImg: baseProperty.image 
      }
    ],
    specifications: DEFAULT_SPECS,
    amenities: DEFAULT_AMENITIES,
    plans: DEFAULT_PLANS
  };

  // State definitions linked to current active data context
  const [selectedUnit, setSelectedUnit] = useState(property.units[0]);
  const [activeImage, setActiveImage] = useState(0);
  const [activeTab, setActiveTab] = useState('videos');
  const [openSpec, setOpenSpec] = useState(property.specifications[0]?.category || '');
  const [linkCopied, setLinkCopied] = useState(false);

  // Synchronize state context if property changes
  useEffect(() => {
    if (property) {
      setSelectedUnit(property.units[0]);
      setOpenSpec(property.specifications[0]?.category || '');
      setActiveImage(0);
      setActiveTab('videos');
    }
    window.scrollTo(0, 0);
  }, [id]);

  // Hero slideshow looping trigger
  useEffect(() => {
    if (!property.images || property.images.length <= 1) return;
    const timer = setInterval(() => {
      setActiveImage((prev) => (prev + 1) % property.images.length);
    }, SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, [activeImage, property.images]);

  // Scrollspy structural layout observer
  useEffect(() => {
    const handleScroll = () => {
      let currentSection = NAV_SECTIONS[0];
      for (const section of NAV_SECTIONS) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 160) {
            currentSection = section;
          }
        }
      }
      setActiveTab((prevTab) => prevTab !== currentSection ? currentSection : prevTab);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    setActiveTab(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 140;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // WhatsApp Handlers using dynamic data
  const handleContactMhada = () => {
    if (!selectedUnit) return;
    const message = `Hello ${property.agencyName}!%0A%0AI am interested in *${property.name}* (${selectedUnit.name} · ${selectedUnit.price}).%0A%0APlease share allocation and cost sheet details.`;
    window.open(`https://wa.me/${property.whatsappNumber}?text=${message}`, '_blank');
  };

  const handleBrochureRequest = () => {
    if (!selectedUnit) return;
    const message = `Hello ${property.agencyName}!%0A%0AI'd like to receive the *brochure and configuration breakdown* for *${property.name}* (${selectedUnit.name} · ${selectedUnit.price}).`;
    window.open(`https://wa.me/${property.whatsappNumber}?text=${message}`, '_blank');
  };

  const handleQuickEnquiry = (e) => {
    e.preventDefault(); 
    const name = e.target['sidebar-name'].value;
    const phone = e.target['sidebar-phone'].value;
    const email = e.target['sidebar-email'].value;

    const message = `*New Lead Enquiry!*%0A%0A*Property:* ${property.name}%0A*Selected Configuration:* ${selectedUnit?.name || 'N/A'}%0A*Name:* ${name}%0A*Phone:* ${phone}%0A*Email:* ${email}%0A%0APlease arrange a direct callback.`;
    window.open(`https://wa.me/${property.whatsappNumber}?text=${message}`, '_blank');
  };

  const handleShare = async () => {
    const shareData = {
      title: property.name,
      text: `Check out this premium property listing: ${property.name} in ${property.location}.`,
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareData.url);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      }
    } catch (err) {}
  };

  return (
    <div className="detail-page pb-20 md:pb-0 bg-stone-50">

      <style>{`
        @keyframes gradient-pan {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient {
          background: linear-gradient(-45deg, #00AEEF, #EC008C, #ED1C24, #00AEEF);
          background-size: 300% 300%;
          animation: gradient-pan 4s ease infinite;
        }
      `}</style>

      {/* --- DESKTOP HERO SECTION --- */}
      <div className="hidden md:block relative">
        <div className="relative h-[520px] lg:h-[580px] overflow-hidden bg-stone-900">
          {property.images.map((img, idx) => (
            <img
              key={idx}
              alt={`${property.name} view ${idx + 1}`}
              className={`absolute inset-0 w-full h-full object-cover detail-hero-ken-burns transition-opacity duration-700 ease-in-out ${
                idx === activeImage ? 'opacity-100' : 'opacity-0'
              }`}
              src={img}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-950/30 to-stone-950/20"></div>
          
          <button onClick={() => navigate(-1)} className="absolute top-6 left-6 z-20 flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 transition shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            Back
          </button>

          <div className="absolute right-6 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-10">
            {property.images.map((img, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setActiveImage(idx)}
                aria-label={`View slider slot ${idx + 1}`}
                className={`relative w-20 h-14 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                  idx === activeImage
                    ? 'border-[#00AEEF] scale-105 shadow-lg shadow-[#00AEEF]/30'
                    : 'border-white/30 opacity-70 hover:opacity-100 hover:border-white/60'
                }`}
              >
                <img alt="" className="w-full h-full object-cover" src={img} />
              </button>
            ))}
          </div>

          <div className="absolute top-6 left-32 right-6 flex justify-between items-start z-10">
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/90 backdrop-blur-md text-white text-xs font-semibold shadow-lg">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z"></path></svg>
                No Brokerage
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-600/90 backdrop-blur-md text-white text-xs font-semibold shadow-lg">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.801 10A10 10 0 1 1 17 3.335"></path><path d="m9 11 3 3L22 4"></path></svg>
                {property.reraStatus}
              </span>
            </div>
            <div className="relative flex gap-2 mr-24">
              <button type="button" onClick={handleShare} className="p-2.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 transition" aria-label="Share profile">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" x2="15.42" y1="13.51" y2="17.49"></line><line x1="15.41" x2="8.59" y1="6.51" y2="10.49"></line></svg>
              </button>
              {linkCopied && (
                <span className="absolute top-full right-0 mt-2 px-3 py-1.5 rounded-lg bg-white text-stone-900 text-xs font-semibold shadow-lg whitespace-nowrap">
                  Link copied!
                </span>
              )}
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-10 z-10">
            <div className="max-w-4xl">
              <p className="text-white/80 text-sm font-medium uppercase tracking-widest mb-2">Residential · {property.status}</p>
              <h1 className="text-3xl lg:text-5xl font-bold text-white tracking-tight leading-tight">{property.name}</h1>
              <p className="mt-3 text-stone-300 text-sm lg:text-base flex items-center gap-2 font-medium">
                <span className="px-2.5 py-1 rounded bg-[#EC008C]/20 text-white font-bold text-xs border border-[#EC008C]/30">{selectedUnit?.name || 'Loading'} Variant</span>
                <span>• {property.location}</span>
              </p>
            </div>
          </div>
        </div>

        {/* --- FLOATING PRICE CARD --- */}
        <div className="relative -mt-6 mx-6 lg:mx-10 z-20">
          <div className="animate-gradient p-[2px] rounded-3xl shadow-2xl">
            <div className="bg-white/95 backdrop-blur-xl rounded-[1.35rem] p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider font-medium">Pricing tier Summary ({selectedUnit?.name})</p>
                <p className="text-3xl lg:text-4xl font-bold text-stone-900 tracking-tight">{selectedUnit?.price || 'Contact for price'}</p>
                <p className="text-sm text-stone-500 mt-1">All Inclusive Registration Pricing Structure</p>
              </div>
              <button type="button" onClick={handleContactMhada} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl animate-gradient text-white font-bold text-sm shadow-lg hover:opacity-90 transition-all">
                Contact Agency
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* --- MOBILE HERO SECTION --- */}
      <div className="block md:hidden">
        <div className="relative h-72 w-full overflow-hidden flex">
          {property.images.map((img, idx) => (
            <img
              key={idx}
              alt="Mobile layout view"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${
                idx === activeImage ? 'opacity-100' : 'opacity-0'
              }`}
              src={img}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-transparent to-stone-950/20"></div>
          <button onClick={() => navigate(-1)} className="absolute top-4 left-4 z-20 flex items-center justify-center w-10 h-10 rounded-full bg-white/20 backdrop-blur-md text-white shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {property.images.map((img, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setActiveImage(idx)}
                aria-label={`Carousel index ${idx + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${
                  idx === activeImage ? 'w-6 animate-gradient' : 'w-2 bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
        <div className="px-4 py-5 space-y-3 bg-white">
          <h1 className="text-2xl font-bold text-stone-900">{property.name}</h1>
          <p className="text-sm text-stone-500">{property.location}</p>
          <div className="flex justify-between items-center pt-2">
            <div>
              <p className="text-xs text-stone-400 uppercase">Selected Plan Value</p>
              <p className="text-2xl font-bold text-stone-900">{selectedUnit?.price}</p>
            </div>
            <span className="px-3 py-1 rounded-full bg-[#EC008C]/10 text-[#EC008C] font-bold text-xs">{selectedUnit?.name}</span>
          </div>
        </div>
      </div>

      {/* --- DYNAMIC STATS BAR --- */}
      <div className="border-y border-stone-200/80 bg-white/80 backdrop-blur-sm mt-4 md:mt-10">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex overflow-x-auto scrollbar-hide py-4 md:py-6 md:justify-center md:flex-wrap gap-6 md:gap-10">
            <div className="flex flex-col items-center text-center min-w-[120px]">
              <div className="w-10 h-10 rounded-xl bg-[#00AEEF]/10 flex items-center justify-center mb-2 text-[#00AEEF]">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8"></path><path d="M4 10V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4"></path></svg>
              </div>
              <p className="text-base font-bold text-stone-900 leading-tight">{selectedUnit?.name || 'Loading'} Layout</p>
              <p className="text-xs text-stone-500 mt-0.5 uppercase tracking-wide">Configuration</p>
            </div>
            <div className="hidden md:block w-px bg-stone-200 self-stretch"></div>
            <div className="flex flex-col items-center text-center min-w-[120px]">
              <div className="w-10 h-10 rounded-xl bg-[#EC008C]/10 flex items-center justify-center mb-2 text-[#EC008C]">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line></svg>
              </div>
              <p className="text-base font-bold text-stone-900 leading-tight">{property.status}</p>
              <p className="text-xs text-stone-500 mt-0.5 uppercase tracking-wide">Possession State</p>
            </div>
            <div className="hidden md:block w-px bg-stone-200 self-stretch"></div>
            <div className="flex flex-col items-center text-center min-w-[120px]">
              <div className="w-10 h-10 rounded-xl bg-[#ED1C24]/10 flex items-center justify-center mb-2 text-[#ED1C24]">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path></svg>
              </div>
              <p className="text-base font-bold text-stone-900 leading-tight">{property.reraStatus}</p>
              <p className="text-xs text-stone-500 mt-0.5 uppercase tracking-wide">Certification</p>
            </div>
            <div className="hidden md:block w-px bg-stone-200 self-stretch"></div>
            <div className="flex flex-col items-center text-center min-w-[120px]">
              <div className="w-10 h-10 rounded-xl bg-[#00AEEF]/10 flex items-center justify-center mb-2 text-[#00AEEF]">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6"></path><path d="m21 3-7 7"></path></svg>
              </div>
              <p className="text-base font-bold text-stone-900 leading-tight">{selectedUnit?.carpet || 'N/A'}</p>
              <p className="text-xs text-stone-500 mt-0.5 uppercase tracking-wide">Carpet Area</p>
            </div>
          </div>
        </div>
      </div>

      {/* --- MAIN STRUCTURE GRID --- */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 md:py-10">
        <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-10">
          
          {/* LEFT CONTENT COLUMN */}
          <main className="flex-1 min-w-0">
            {/* Scrollspy Header Nav */}
            <nav className="sticky top-[72px] z-30 -mx-4 px-4 lg:mx-0 lg:px-0 py-3 bg-stone-50/90 backdrop-blur-md border-b border-stone-200/60 mb-6">
              <div className="flex gap-2 overflow-x-auto scrollbar-hide flex-nowrap">
                {NAV_SECTIONS.map((tab) => (
                  <button 
                    key={tab}
                    onClick={() => scrollToSection(tab)}
                    type="button" 
                    className={`shrink-0 whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 capitalize ${
                      activeTab === tab 
                        ? 'animate-gradient text-white shadow-md' 
                        : 'bg-white text-stone-600 border border-stone-200 hover:border-[#EC008C] hover:text-[#EC008C]'
                    }`}
                  >
                    {tab.replace('-', ' ')}
                  </button>
                ))}
              </div>
            </nav>

            <div className="space-y-6">

              {/* --- DYNAMIC PROJECT VIDEOS --- */}
              <section id="videos" className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-stone-200 scroll-mt-24">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-6 animate-gradient rounded-full"></div>
                  <h3 className="text-xl font-bold text-stone-900">Project Videos</h3>
                </div>
                
                {property.videos && property.videos.length > 0 ? (
                  <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 snap-x snap-mandatory">
                    {property.videos.map((video) => (
                      <div 
                        key={video.id} 
                        className="min-w-[280px] md:min-w-[320px] w-[80vw] md:w-[320px] shrink-0 snap-start bg-white rounded-xl overflow-hidden border border-stone-200 shadow-sm transition-all hover:shadow-md hover:border-[#00AEEF] flex flex-col"
                      >
                        <div className="relative w-full aspect-video bg-stone-100">
                          <iframe
                            className="w-full h-full absolute inset-0"
                            src={video.embedUrl}
                            title={video.title}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            loading="lazy"
                          ></iframe>
                        </div>
                        <div className="p-4 bg-stone-50 border-t border-stone-100">
                          <h4 className="font-semibold text-stone-800 text-sm line-clamp-1">{video.title}</h4>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-stone-400 text-sm">No digital media tours uploaded for this site.</p>
                )}
              </section>

              {/* --- DYNAMIC DETAILED OVERVIEW --- */}
              <section id="overview" className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-stone-200 scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-1 h-6 animate-gradient rounded-full"></div>
                  <h3 className="text-xl font-bold text-stone-900">Project Overview</h3>
                </div>
                
                <div className="border-t border-stone-100 pt-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-4">
                    <OverviewItem title="Ownership Structure" value={property.ownership} icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>} />
                    <OverviewItem title="Estimated Possession" value={property.possession} icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>} />
                    <OverviewItem title="Property Timeline" value={property.propertyStatus} icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path></svg>} />
                    <OverviewItem title="Elevator Access" value={property.elevator} icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 9l4-4 4 4"/><path d="M16 15l-4 4-4-4"/></svg>} />
                    <OverviewItem title="Category Core" value={property.category} highlighted={true} icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path></svg>} />
                    <OverviewItem title="Architectural Type" value={property.type} icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect></svg>} />
                    <OverviewItem title="Gated Infrastructure" value={property.gatedSociety} icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="20" width="20" height="2"></rect></svg>} />
                    <OverviewItem title="Pricing Flexibility" value={property.negotiable} icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m11 17 2 2a1 1 0 1 0 3-3"/></svg>} />
                    <OverviewItem title="Banking Loan Support" value={property.loanFacility} icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3"></path></svg>} />
                    <OverviewItem title="Legal Documentation" value={property.documents} icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5"></path></svg>} />
                    <OverviewItem title="Estimated Maintenance" value={property.maintenance} icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7H5"></path></svg>} />
                    <OverviewItem title="Dedicated Parking Space" value={property.dedicatedParking} icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 16H9m10 0h3"></path></svg>} />
                    <OverviewItem title="Open Visitors Space" value={property.openParking} icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 13v3"></path></svg>} />
                    <OverviewItem title="Integrated Retail Space" value={property.commercialSpace} icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.17 3.22a2 2 0 0 0-2.83 0L3.22 18.34" /></svg>} />
                  </div>
                </div>
              </section>

              {/* --- DYNAMIC INTERACTIVE PRICE CONFIGURATIONS --- */}
              <section id="pricing" className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-stone-200 scroll-mt-24">
                <h3 className="text-xl font-bold text-stone-900 mb-2">Available Tiers & Pricing</h3>
                <p className="text-xs text-stone-400 mb-6">Select a configuration to update specifications, down-payments, and associated structural layouts across the page.</p>
                <div className="flex gap-4 flex-wrap">
                  {property.units.map((unit) => {
                    const isSelected = selectedUnit?.id === unit.id;
                    return (
                      <button
                        key={unit.id}
                        type="button"
                        onClick={() => setSelectedUnit(unit)}
                        className={`relative p-[2px] rounded-[1.35rem] transition-all text-left min-w-[240px] flex-1 sm:flex-initial outline-none ${
                          isSelected 
                            ? 'animate-gradient shadow-lg scale-105' 
                            : 'bg-stone-200 hover:bg-stone-300'
                        }`}
                      >
                        <div className={`h-full w-full rounded-[1.25rem] p-5 ${isSelected ? 'bg-white' : 'bg-stone-50'}`}>
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="text-base font-bold text-stone-800">{unit.name}</h4>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${isSelected ? 'bg-[#EC008C]/10 text-[#EC008C]' : 'bg-stone-200 text-stone-600'}`}>
                              {unit.status}
                            </span>
                          </div>
                          <p className="text-[11px] text-stone-400 uppercase tracking-wide mb-1">Carpet Area</p>
                          <p className="text-sm text-stone-600 mb-3">{unit.carpet}</p>
                          <p className={`text-lg font-bold ${isSelected ? 'text-[#EC008C]' : 'text-stone-700'}`}>{unit.price}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* --- DYNAMIC SPECIFICATIONS --- */}
              <section id="specifications" className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-stone-200 scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-1 h-6 animate-gradient rounded-full"></div>
                  <h3 className="text-xl font-bold text-stone-900">Project Specifications</h3>
                </div>
                
                <div className="border-t border-stone-100 pt-2">
                  {property.specifications.map((spec) => (
                    <div key={spec.category} className="border-b border-stone-100 last:border-0">
                      <button
                        type="button"
                        onClick={() => setOpenSpec(openSpec === spec.category ? '' : spec.category)}
                        className="w-full flex items-center justify-between py-4 text-left transition-colors hover:bg-stone-50/50"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-[#EC008C]">{getSpecIcon(spec.category)}</span>
                          <span className="font-medium text-stone-900">{spec.category}</span>
                        </div>
                        <span className="text-stone-400">
                          {openSpec === spec.category ? <ChevronUp /> : <ChevronDown />}
                        </span>
                      </button>
                      
                      {openSpec === spec.category && (
                        <div className="pb-5 pt-1 px-2 md:px-4 space-y-3.5 animate-fadeIn">
                          {spec.details.map((detail, idx) => (
                            <div key={idx} className="flex flex-col sm:flex-row justify-between sm:items-center text-sm gap-1">
                              <span className="text-stone-500">{detail.label}</span>
                              <span className="text-stone-900 font-medium sm:text-right">{detail.value}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* --- DYNAMIC AMENITIES GRID --- */}
              <section id="amenities" className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200 scroll-mt-24">
                <h3 className="text-xl font-bold text-stone-900 mb-6">Amenities & Structural Features</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
                  {property.amenities.map((amenity, idx) => (
                    <div key={idx} className="flex flex-col items-center justify-center p-3 rounded-xl border border-stone-100 hover:border-[#00AEEF] hover:shadow-sm transition bg-stone-50/50 cursor-default">
                      <img alt={amenity.label} className="w-8 h-8 object-contain mb-2" src={amenity.img} />
                      <p className="text-xs text-stone-600 font-medium text-center leading-tight">{amenity.label}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* --- DYNAMIC ABOUT DESCRIPTION --- */}
              <section id="about" className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200 scroll-mt-24">
                <h3 className="text-xl font-bold text-stone-900 mb-4">About This Property</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{property.about}</p>
              </section>

              {/* --- DYNAMIC FLOOR PLANS --- */}
              <section id="floor-plans" className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200 scroll-mt-24">
                <h3 className="text-xl font-bold text-stone-900 mb-6">Floor Layout Structure ({selectedUnit?.name || 'Loading'})</h3>
                {selectedUnit ? (
                  <div className="border border-stone-200 rounded-xl p-5 max-w-sm bg-stone-50/50">
                    <p className="text-lg font-bold text-stone-900">{selectedUnit.carpet}</p>
                    <p className="text-xs text-stone-500 mb-3">Carpet Area Blueprint Arrangement</p>
                    <div className="w-full rounded-lg overflow-hidden border border-stone-200 mb-3 bg-white">
                      <img alt={`${selectedUnit.name} layout map`} className="w-full h-auto max-h-[160px] object-contain mx-auto" src={selectedUnit.floorPlanImg} />
                    </div>
                    <p className="text-base font-bold text-[#EC008C] mb-4">{selectedUnit.price}</p>
                    <button type="button" onClick={handleBrochureRequest} className="w-full flex items-center justify-center gap-2 text-sm font-bold text-white animate-gradient shadow-md hover:opacity-90 px-4 py-3 rounded-xl transition">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 17V3"></path><path d="m6 11 6 6 6-6"></path><path d="M19 21H5"></path></svg> 
                       Download Technical Specifications
                    </button>
                  </div>
                ) : (
                  <p className="text-stone-400 text-sm">Select a configuration to review blueprints.</p>
                )}
              </section>

              {/* --- CONNECTIVITY MAPS / SCHEMATICS --- */}
              <section id="location-plan" className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200 scroll-mt-24">
                <div className="flex items-center gap-2 mb-2">
                  <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#00AEEF]/10 text-[#00AEEF]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0"></path><circle cx="12" cy="10" r="3"></circle></svg>
                  </span>
                  <h3 className="text-xl font-bold text-stone-900">Location Plan</h3>
                </div>
                <p className="text-sm text-stone-500 mb-5">Connectivity and neighborhood perimeters around {property.name}.</p>
                <div className="overflow-hidden rounded-xl border border-stone-200 bg-stone-50">
                  <img alt={`${property.name} Location overview`} className="w-full h-auto object-contain max-h-[400px] mx-auto" loading="lazy" src={property.plans.locationPlan} />
                </div>
              </section>

              <section id="layout" className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200 scroll-mt-24">
                <div className="flex items-center gap-2 mb-2">
                  <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#EC008C]/10 text-[#EC008C]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect></svg>
                  </span>
                  <h3 className="text-xl font-bold text-stone-900">Master Site Layout</h3>
                </div>
                <p className="text-sm text-stone-500 mb-5">Overall plot arrangement blueprint and wing configurations.</p>
                <div className="overflow-hidden rounded-xl border border-stone-200 bg-stone-50">
                  <img alt={`${property.name} site layout`} className="w-full h-auto object-contain max-h-[400px] mx-auto" loading="lazy" src={property.plans.layout} />
                </div>
              </section>

              <section id="ground-floor" className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200 scroll-mt-24">
                <div className="flex items-center gap-2 mb-2">
                  <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#ED1C24]/10 text-[#ED1C24]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect></svg>
                  </span>
                  <h3 className="text-xl font-bold text-stone-900">Ground Floor Plan</h3>
                </div>
                <p className="text-sm text-stone-500 mb-5">Lobby, utilities allocation mapping, and commercial tier entry structures.</p>
                <div className="overflow-hidden rounded-xl border border-stone-200 bg-stone-50">
                  <img alt="Ground layout schematics" className="w-full h-auto object-contain max-h-[400px] mx-auto" loading="lazy" src={property.plans.groundFloor} />
                </div>
              </section>

              <section id="typical-floor" className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200 scroll-mt-24">
                <div className="flex items-center gap-2 mb-2">
                  <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#00AEEF]/10 text-[#00AEEF]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><path d="M3 9h18"></path></svg>
                  </span>
                  <h3 className="text-xl font-bold text-stone-900">Typical Floor Plan</h3>
                </div>
                <p className="text-sm text-stone-500 mb-5">Standard intermediate residential unit tracking plan layout configurations.</p>
                <div className="overflow-hidden rounded-xl border border-stone-200 bg-stone-50">
                  <img alt="Standard repeating intermediate plan" className="w-full h-auto object-contain max-h-[400px] mx-auto" loading="lazy" src={property.plans.typicalFloor} />
                </div>
              </section>

            </div>
          </main>

          {/* --- STICKY SIDEBAR DYNAMIC FORM --- */}
          <aside className="hidden lg:block w-[360px] shrink-0 sticky top-28 h-fit self-start">
            <div className="animate-gradient p-[2px] rounded-3xl shadow-xl">
              <div className="bg-white rounded-[1.35rem] overflow-hidden">
                
                <div className="animate-gradient p-6 text-white text-center">
                  <p className="text-white/90 text-xs font-bold uppercase tracking-widest mb-1 drop-shadow-sm">Interested?</p>
                  <h2 className="text-xl font-extrabold leading-snug drop-shadow-sm line-clamp-1">{property.name}</h2>
                  <p className="text-white/90 text-sm mt-1 font-medium drop-shadow-sm">Allocation Agency: {property.agencyName}</p>
                </div>
                
                <div className="p-5 space-y-3 border-b border-stone-100">
                  <button type="button" onClick={handleContactMhada} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-stone-900 text-white font-bold text-sm shadow-md hover:bg-stone-800 transition">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3z"></path></svg>
                    Contact Representatives
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={handleContactMhada} className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-stone-200 text-stone-700 text-xs font-medium hover:border-[#EC008C] hover:text-[#EC008C] transition">
                      WhatsApp Channel
                    </button>
                    <button type="button" className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-stone-200 text-stone-700 text-xs font-medium hover:border-[#00AEEF] hover:text-[#00AEEF] transition">
                      Schedule Tour
                    </button>
                  </div>
                </div>

                <form onSubmit={handleQuickEnquiry} className="p-5 space-y-4">
                  <p className="text-sm font-semibold text-stone-800">Quick Request Allocation Details</p>
                  <div>
                    <label htmlFor="sidebar-name" className="block text-xs font-medium text-stone-600 mb-1">Name <span className="text-red-500">*</span></label>
                    <input id="sidebar-name" required maxLength="50" placeholder="Your name" className="w-full px-3 py-2.5 rounded-lg border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#00AEEF]/40 focus:border-[#00AEEF]" type="text" />
                  </div>
                  <div>
                    <label htmlFor="sidebar-phone" className="block text-xs font-medium text-stone-600 mb-1">Phone <span className="text-red-500">*</span></label>
                    <input id="sidebar-phone" required pattern="[0-9]{10}" placeholder="10-digit primary number" className="w-full px-3 py-2.5 rounded-lg border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#00AEEF]/40 focus:border-[#00AEEF]" type="tel" />
                  </div>
                  <div>
                    <label htmlFor="sidebar-email" className="block text-xs font-medium text-stone-600 mb-1">Email <span className="text-red-500">*</span></label>
                    <input id="sidebar-email" required placeholder="you@domain.com" className="w-full px-3 py-2.5 rounded-lg border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#00AEEF]/40 focus:border-[#00AEEF]" type="email" />
                  </div>
                  <button type="submit" className="w-full py-3 rounded-xl animate-gradient text-white font-bold text-sm hover:opacity-90 transition shadow-md">Submit Lead Profile</button>
                  <p className="flex items-center gap-1.5 text-xs text-stone-400 justify-center mt-2">
                    Encrypted Verification Secure Protocol Enabled
                  </p>
                </form>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* --- MOBILE FIXED BAR --- */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white/95 backdrop-blur-lg border-t border-stone-200 px-4 py-3 flex items-center justify-between gap-3 shadow-[0_-8px_30px_rgba(0,0,0,0.08)]">
        <div>
          <p className="text-xs text-stone-500">Selected Option Value</p>
          <p className="text-lg font-bold text-stone-900">{selectedUnit?.price || 'Get Price'}</p>
        </div>
        <button type="button" onClick={handleContactMhada} className="flex-1 max-w-[200px] flex items-center justify-center gap-2 py-3 rounded-xl animate-gradient text-white font-bold text-sm shadow-md">
          Connect Securely
        </button>
      </div>

    </div>
  );
}

// Subcomponent Layout Blocks
const OverviewItem = ({ title, value, icon, highlighted }) => (
  <div className={`flex items-center gap-4 ${highlighted ? 'bg-[#00AEEF]/5 p-3 rounded-2xl -m-3' : ''}`}>
    <div className="w-12 h-12 rounded-[14px] bg-[#EC008C]/10 flex items-center justify-center text-[#EC008C] shrink-0">
      {icon}
    </div>
    <div>
      <p className="text-[11px] text-stone-400 uppercase tracking-widest font-medium mb-0.5">{title}</p>
      <p className="text-[15px] font-semibold text-stone-900 leading-tight">{value}</p>
    </div>
  </div>
);

const AmenityItem = ({ img, label }) => (
  <div className="flex flex-col items-center justify-center p-3 rounded-xl border border-stone-100 hover:border-[#00AEEF] hover:shadow-sm transition bg-stone-50/50 cursor-default">
    <img alt={label} className="w-8 h-8 object-contain mb-2" src={img} />
    <p className="text-xs text-stone-600 font-medium text-center leading-tight">{label}</p>
  </div>
);